import { useEffect, useMemo, useState } from "react";
import { Building2, ChevronLeft, ChevronRight, Loader2, Mail, MoreVertical, Pencil, Search, Shield, Trash2, UserCheck, UserMinus, UserPlus, Users } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AdminPasswordDialog from "@/components/AdminPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;
const roleOptions = [
  ["user", "Regular User"],
  ["admin", "Administrator"],
  ["planning_analyst", "Planning Analyst"],
  ["daily_report", "Daily Report"],
  ["financial_analyst", "Financial Analyst"],
  ["gst_report", "GST Report"],
  ["supply_chain_analyst", "Supply Chain Analyst"],
  ["purchasing_analyst", "Purchasing Analyst"],
  ["ai_config", "AI Configuration"],
  ["organizations", "Organizations"],
  ["audit_log", "Audit Logs"]
];
const roleLabel = (role) => ({
  admin: "Administrator",
  planning_analyst: "Planning",
  daily_report: "Daily Report",
  financial_analyst: "Finance",
  gst_report: "GST Report",
  supply_chain_analyst: "Supply Chain",
  purchasing_analyst: "Purchasing",
  ai_config: "AI Config",
  organizations: "Organizations",
  audit_log: "Audit Log",
  user: "User"
}[role] || role);


const userRoles = (user) => user.roles?.length ? user.roles : [user.role || "user"];
const isAdminUser = (user) => userRoles(user).includes("admin");
const loginLabel = (user) => isAdminUser(user) ? "Password" : "Email OTP";

const StatCard = ({ label, value, caption, icon: Icon, color }) => (
  <Card className="border-slate-200 shadow-sm">
    <CardContent className="flex items-start gap-3 p-4">
      <div className={`rounded-lg p-2.5 ${color}`}><Icon className="h-5 w-5" /></div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tracking-tight">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
      </div>
    </CardContent>
  </Card>
);

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({ username: "", email: "", password: "", role: "user", roles: ["user"], tenant_id: "" });
  const [editingUser, setEditingUser] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  
  // Admin password dialog state
  const [adminAuthModal, setAdminAuthModal] = useState({
    open: false,
    title: "",
    description: "",
    actionText: "Confirm",
    variant: "default",
    onConfirm: null,
  });

  const isAdminAccount = (formData.roles || [formData.role]).includes("admin");

  const promptAdminAuth = ({ title, description, actionText = "Confirm", variant = "default", onConfirm }) => {
    setAdminAuthModal({
      open: true,
      title,
      description,
      actionText,
      variant,
      onConfirm,
    });
  };

  const fetchUsers = async () => {
    try {
      setUsers((await axios.get(`${API}/auth/users`)).data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      setTenants((await axios.get(`${API}/tenants`)).data);
    } catch {
      setTenants([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchTenants();
  }, []);

  useEffect(() => setPage(1), [search, roleFilter, statusFilter]);

  const handleRoleToggle = (role) => setFormData((previous) => {
    const currentRoles = previous.roles?.length ? previous.roles : [previous.role || "user"];
    const roles = currentRoles.includes(role) ? currentRoles.filter((value) => value !== role) : [...currentRoles, role];
    const nextRoles = roles.length ? roles : ["user"];
    return { ...previous, role: nextRoles.includes("admin") ? "admin" : nextRoles[0], roles: nextRoles };
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    const roles = formData.roles?.length ? formData.roles : [formData.role || "user"];
    const admin = roles.includes("admin");

    promptAdminAuth({
      title: "Authorize User Creation",
      description: `Confirm administrator password to create ${admin ? "administrator account" : "user account"} '${formData.username}'.`,
      actionText: admin ? "Create Administrator" : "Create OTP User",
      onConfirm: async () => {
        setSubmitting(true);
        try {
          const payload = { ...formData, role: admin ? "admin" : roles[0], roles };
          if (!payload.tenant_id) delete payload.tenant_id;
          if (admin) delete payload.email;
          else delete payload.password;
          await axios.post(`${API}/auth/register`, payload);
          toast.success(admin ? "Administrator created" : "OTP user created");
          setFormData({ username: "", email: "", password: "", role: "user", roles: ["user"], tenant_id: "" });
          setDialogOpen(false);
          fetchUsers();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to create user");
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const openEmailEditor = (user) => {
    setEditingUser(user);
    setEditEmail(user.email || "");
  };

  const updateEmail = (event) => {
    event.preventDefault();
    if (!editingUser) return;

    promptAdminAuth({
      title: "Authorize Email Update",
      description: `Confirm administrator password to update OTP email for '${editingUser.username}'.`,
      actionText: "Save Email",
      onConfirm: async () => {
        setEditSubmitting(true);
        try {
          await axios.put(`${API}/auth/users/${editingUser.id}`, { email: editEmail });
          toast.success("OTP email updated");
          setEditingUser(null);
          fetchUsers();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to update OTP email");
        } finally {
          setEditSubmitting(false);
        }
      }
    });
  };

  const handleDelete = (id, username) => {
    promptAdminAuth({
      title: "Authorize User Deletion",
      description: `Confirm administrator password to permanently delete user '${username}'.`,
      actionText: "Delete User",
      variant: "destructive",
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/auth/users/${id}`);
          toast.success("User deleted");
          fetchUsers();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to delete user");
        }
      }
    });
  };

  const handleToggleStatus = (id, username, isActive) => {
    promptAdminAuth({
      title: `Authorize ${isActive ? "Disabling" : "Enabling"} User`,
      description: `Confirm administrator password to ${isActive ? "disable" : "enable"} account '${username}'.`,
      actionText: isActive ? "Disable User" : "Enable User",
      variant: isActive ? "destructive" : "default",
      onConfirm: async () => {
        try {
          const response = await axios.put(`${API}/auth/users/${id}/toggle`);
          toast.success(response.data.message);
          fetchUsers();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to update user");
        }
      }
    });
  };

  const filtered = useMemo(() => users.filter((item) => {
    const roles = userRoles(item);
    const query = search.toLowerCase();
    return (item.username.toLowerCase().includes(query) || item.email?.toLowerCase().includes(query)) &&
      (roleFilter === "all" || roles.includes(roleFilter)) &&
      (statusFilter === "all" || (statusFilter === "active") === Boolean(item.is_active));
  }), [users, search, roleFilter, statusFilter]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeCount = users.filter((item) => item.is_active).length;
  const adminCount = users.filter(isAdminUser).length;

  return (
    <div className="space-y-5" data-testid="user-management">
      <AdminPasswordDialog
        open={adminAuthModal.open}
        onOpenChange={(open) => setAdminAuthModal(prev => ({ ...prev, open }))}
        title={adminAuthModal.title}
        description={adminAuthModal.description}
        actionText={adminAuthModal.actionText}
        variant={adminAuthModal.variant}
        onConfirm={adminAuthModal.onConfirm}
      />

      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-2xl font-semibold normal-case tracking-tight">Users</h2>
          <p className="text-sm text-muted-foreground">Manage OTP user accounts and administrator access.</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button"><UserPlus className="mr-2 h-4 w-4" />Add User</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[500px]" data-testid="add-user-dialog">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                {isAdminAccount ? "Administrators sign in with a password." : "Users sign in with a one-time code delivered to their work email."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-3 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input id="username" value={formData.username} onChange={(event) => setFormData((value) => ({ ...value, username: event.target.value }))} required data-testid="input-new-username" />
              </div>
              <div className="space-y-2">
                <Label>Role Permissions (Select one or more)</Label>
                <div className="grid grid-cols-2 gap-2.5 border rounded-md p-3 bg-slate-50/80 dark:bg-slate-900/40">
                  {roleOptions.map(([value, label]) => {
                    const checked = (formData.roles || [formData.role]).includes(value);
                    return (
                      <label key={value} className="flex items-center gap-2 text-xs cursor-pointer select-none">
                        <input type="checkbox" checked={checked} onChange={() => handleRoleToggle(value)} className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4" data-testid={`checkbox-role-${value}`} />
                        <span className={checked ? "font-bold text-primary" : "font-medium text-slate-700 dark:text-slate-200"}>{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {isAdminAccount ? (
                <div className="space-y-2">
                  <Label htmlFor="password">Administrator Password</Label>
                  <Input id="password" type="password" minLength={8} value={formData.password} onChange={(event) => setFormData((value) => ({ ...value, password: event.target.value }))} required data-testid="input-new-password" />
                  <p className="text-xs text-muted-foreground">At least 8 characters. Only administrators use passwords.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="email">Work Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input id="email" type="email" className="pl-9" value={formData.email} onChange={(event) => setFormData((value) => ({ ...value, email: event.target.value }))} required data-testid="input-new-email" />
                  </div>
                  <p className="text-xs text-muted-foreground">Required for one-time-code sign in.</p>
                </div>
              )}
              <DialogFooter>
                <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                <Button type="submit" disabled={submitting} data-testid="submit-new-user">
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isAdminAccount ? "Create Administrator" : "Create OTP User"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={Boolean(editingUser)} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Update OTP Email</DialogTitle>
            <DialogDescription>Set the work email used by {editingUser?.username} to receive sign-in codes.</DialogDescription>
          </DialogHeader>
          <form onSubmit={updateEmail} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-email">Work Email</Label>
              <Input id="edit-email" type="email" value={editEmail} onChange={(event) => setEditEmail(event.target.value)} required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
              <Button type="submit" disabled={editSubmitting}>
                {editSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Email
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total Users" value={users.length} caption="All system users" icon={Users} color="bg-blue-50 text-blue-600" />
        <StatCard label="Active Users" value={activeCount} caption="Currently active" icon={UserCheck} color="bg-emerald-50 text-emerald-600" />
        <StatCard label="Inactive Users" value={users.length - activeCount} caption="Currently inactive" icon={UserMinus} color="bg-amber-50 text-amber-600" />
        <StatCard label="Administrators" value={adminCount} caption="Password sign-in accounts" icon={Shield} color="bg-violet-50 text-violet-600" />
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(260px,1fr)_180px_180px]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search users by name or email..." className="pl-9" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roleOptions.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>User</TableHead>
                      <TableHead>Sign In</TableHead>
                      <TableHead>Roles</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-20 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visible.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="h-32 text-center text-muted-foreground">No users match the selected filters.</TableCell></TableRow>
                    ) : visible.map((item) => (
                      <TableRow key={item.id} className={!item.is_active ? "bg-muted/20" : ""} data-testid={`user-row-${item.username}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 font-medium text-slate-600">
                              {item.username.slice(0, 1).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium">{item.username} {item.id === currentUser?.id && <Badge variant="secondary" className="ml-1 text-[10px]">You</Badge>}</p>
                              <p className="text-xs text-muted-foreground">{item.email || (isAdminUser(item) ? "Administrator account" : "Email required")}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="secondary" className="font-normal">{loginLabel(item)}</Badge></TableCell>
                        <TableCell><div className="flex flex-wrap gap-1">{userRoles(item).map((role) => <Badge key={role} variant="secondary" className="font-normal">{roleLabel(role)}</Badge>)}</div></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{item.tenant_name ? <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{item.tenant_name}</span> : "Not assigned"}</TableCell>
                        <TableCell><Badge className={item.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-red-50 text-red-700 hover:bg-red-50"}>{item.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                        <TableCell className="text-right">
                          {item.id !== currentUser?.id && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label={`Actions for ${item.username}`}><MoreVertical className="h-4 w-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {!isAdminUser(item) && (
                                  <DropdownMenuItem onClick={() => openEmailEditor(item)}>
                                    <Pencil className="mr-2 h-4 w-4" />Set OTP Email
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleToggleStatus(item.id, item.username, item.is_active)} data-testid={`toggle-user-${item.username}`}>
                                  {item.is_active ? <UserMinus className="mr-2 h-4 w-4" /> : <UserCheck className="mr-2 h-4 w-4" />}
                                  {item.is_active ? "Disable" : "Enable"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDelete(item.id, item.username)} className="text-destructive" data-testid={`delete-user-${item.username}`}>
                                  <Trash2 className="mr-2 h-4 w-4" />Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex flex-col items-center justify-between gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row">
                <span>Showing {filtered.length ? (page - 1) * PAGE_SIZE + 1 : 0} to {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} users</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" className="h-8 w-8" disabled={page === 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="min-w-16 text-center">{page} / {pages}</span>
                  <Button variant="outline" size="icon" className="h-8 w-8" disabled={page === pages} onClick={() => setPage((value) => value + 1)}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;
